// A kiadás-sor KATEGÓRIA cellája — helyben szerkeszthető, KERESŐS választó.
//
// Miért a cellában és nem modálban: a lap fő munkája a besorolás (havonta sok száz sor), és egy modál
// megnyitása/bezárása soronként két extra kattintás.
//
// A NAV-soron is engedett: ez az EGYETLEN írás-út, amit az adóhatóság rekordján is megnyitunk.
//
// ⚠️ A CELLA NEM MENT. A választás csak a sor FÜGGŐ állapotát állítja; a mentést a sor végén álló „Mentés"
// gomb indítja (user-döntés). MIÉRT: a kattintásra induló írás egy elvétett választást azonnal kiküldött, és
// a visszavonás egy MÁSODIK írás volt (két verzió-lépés a bizonylaton). A függő állapot a listát birtokló
// hookban él, mert a kategóriát és a vonatkozási hónapot EGY kérés menti — két cella, egy írás.

import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { Text } from '@/components/ui/text';
import { categoryLabel, categoryLeafOptions, UNCATEGORIZED } from '@/features/expenses/api';

import type { ExpenseCategoryName, ExpenseCategoryRow, ExpenseRow } from '@atlas/contracts';

export interface ExpenseCategoryCellProps {
  row: ExpenseRow;
  categories: readonly ExpenseCategoryRow[];
  /** A MEGJELENÍTENDŐ érték: a függő választás, ha van, különben a soré. */
  value: string | null;
  /** A választás — a hívó a sor függő állapotába írja (nem ment). */
  onChange: (categoryId: string | null) => void;
  canManage: boolean;
}

/**
 * A kategória ŐS-LÁNCA (gyökértől a szülőig) és az ág GYÖKERE — a legördülő alatti halvány út forrása.
 *
 * MIÉRT ITT, és nem a sor mezőiből: függő választásnál a soron még a RÉGI besorolás áll, az út viszont a
 * VÁLASZTOTTAT kell mutassa — különben a kezelő azt hinné, elveszett a hovatartozás. A `seen` halmaz a
 * (hibás adatból eredő) kör-hivatkozás ellen véd.
 */
function ancestryOf(
  categoryId: string | null,
  categories: readonly ExpenseCategoryRow[],
): { path: ExpenseCategoryName[]; name: ExpenseCategoryName | null } {
  if (categoryId === null) return { path: [], name: null };
  const picked = categories.find((c) => c.id === categoryId);
  const path: ExpenseCategoryName[] = [];
  const seen = new Set<string>();
  let cursor = picked?.parentId ?? null;
  while (cursor !== null && !seen.has(cursor)) {
    seen.add(cursor);
    const node = categories.find((c) => c.id === cursor);
    if (!node) break;
    path.unshift(node.name);
    cursor = node.parentId;
  }
  return { path, name: picked?.name ?? null };
}

export function ExpenseCategoryCell({
  row,
  categories,
  value,
  onChange,
  canManage,
}: ExpenseCategoryCellProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { path, name } = ancestryOf(value, categories);

  if (!canManage) {
    return (
      <div className="min-w-0">
        {row.categoryId === null ? (
          <Text variant="secondary" as="span" className="text-xs">
            {t('expenses.uncategorized')}
          </Text>
        ) : (
          <span className="truncate">{categoryLabel(row.categoryName, locale)}</span>
        )}
        {row.categoryPath.length > 0 && (
          <Text variant="secondary" tone="tertiary" as="div" className="truncate text-xs">
            {row.categoryPath.map((n) => categoryLabel(n, locale)).join(' / ')}
          </Text>
        )}
      </div>
    );
  }

  // CSAK LEVÉL-kategória választható (l. `categoryLeafOptions`), és a kereső az ÁGRA is illeszt — a „bér"
  // szóra például az összes bér-ág alatti tétel előjön, az útjával.
  //
  // A „BESOROLATLAN” a lista ELSŐ választható eleme (user-lelet): enélkül egy elvétett kattintást csak a
  // törlés-X-szel lehetett visszavonni, azt viszont a kezelő nem találta meg. A visszaállítás ugyanott
  // van, ahol a választás történt.
  const leaves = categoryLeafOptions(categories, locale);
  const options = [{ id: UNCATEGORIZED, label: t('expenses.uncategorized') }, ...leaves];
  const selected = value === null ? null : (leaves.find((o) => o.id === value) ?? null);

  return (
    <div className="min-w-0 space-y-1">
      <EntityCombobox
        value={value ?? UNCATEGORIZED}
        onChange={(next) => onChange(next === UNCATEGORIZED ? null : next)}
        options={options}
        initialOption={
          selected ??
          (value === null
            ? { id: UNCATEGORIZED, label: t('expenses.uncategorized') }
            : {
                id: value,
                label: categoryLabel(name, locale),
                sublabel: path.map((n) => categoryLabel(n, locale)).join(' / '),
              })
        }
        clearable={false}
        placeholder={t('expenses.uncategorized')}
        aria-label={t('expenses.classifyAria', { supplier: row.supplierName })}
      />
      {/* AZ ÁG A VÁLASZTÓBAN áll, halvány kisbetűvel — NEM külön sorban alatta. Mért hiba (user-lelet): a
          külön sor csak a besorolt sorokon jelent meg, és eltolta a mezőt, amitől a lista „ugrált". */}
    </div>
  );
}
